import {
  BookingServiceApi,
  BookingServiceV2Api,
  Configuration,
} from '@ennismore/ows-api-client';
import axios, { Axios, AxiosError } from 'axios';
import { ZodError } from 'zod';

import { getClientHeaders } from '@/api/headers';
import { buildBaseUrl } from '@/api/helpers/build-base-url';
import { EnnismoreAPIClientConfig } from '@/api/interfaces';
import { APIError } from '@/errors';

import { parseOWSAxiosError } from './errors';

/**
 * Parent of all OWS / legacy API services.
 * Handles setting up of client + common request options.
 */
export abstract class OWSBookingAPIBaseService {
  client: BookingServiceApi;
  clientv2: BookingServiceV2Api;
  config: Configuration;
  rawClient: Axios;

  get options() {
    return {
      headers: {
        'x-api-key': this.credentials.apiKey,
        'x-skip-redirect': this.credentials.skipRedirect ? 'true' : undefined,
        ...getClientHeaders(this.credentials),
      },
    };
  }

  constructor(protected readonly credentials: EnnismoreAPIClientConfig) {
    const baseUrlWithoutV2 = credentials.baseUrl.replace('/v2', '');

    const basePath = buildBaseUrl(baseUrlWithoutV2, {
      servicePrefix: 'gateway/ows',
      dialect: 'ows',
    });

    // OHIP Booking API is the same URL as OWS, minus the version number.
    // We should flip this behaviour when we switch to OHIP-dominant approach.
    this.config = new Configuration({
      basePath,
    });

    this.rawClient = axios.create({
      baseURL: this.config.basePath,
      headers: this.options.headers,
    });

    this.client = new BookingServiceApi(this.config);
    this.clientv2 = new BookingServiceV2Api(this.config);
  }

  protected parseError(error: AxiosError<unknown> | ZodError): APIError {
    return parseOWSAxiosError(error);
  }
}
